import type { FC, ReactNode } from 'react';
import React from 'react';
import { observer } from 'mobx-react-lite';

import { useUserStore } from 'src/services/auth/auth';
import { routes } from 'src/services/routing';
import UserState from 'src/services/state/User';
import LoadingScreen from 'src/components/LoadingScreen/LoadingScreen';

interface AuthGuardProps {
  children?: ReactNode;
}

/**
 * Shows nested views only to logged in users and redirects logged out users accordingly.
 *
 * Auth state is managed by the ai-kit-auth UserStore which calls /auth/me/ on mount.
 * This component gates on that result — no retries or polling needed.
 *
 * When offline (no server response), falls back to locally cached user state
 * instead of redirecting to a login page that can't be reached.
 */
const AuthGuard: FC<AuthGuardProps> = ({ children }) => {
  const { loading, loggedIn, csrf } = useUserStore();
  const noResponse = !csrf;

  // Still waiting for the server to respond.
  if (loading) {
    return <LoadingScreen />;
  }

  // Server responded and confirmed the user is logged in.
  if (loggedIn) {
    return <>{children}</>;
  }

  // Server never responded (offline / config.js failed to load).
  // Fall back to locally cached user state so the app remains usable offline.
  if (noResponse && UserState.user) {
    return <>{children}</>;
  }

  // Not logged in — redirect to backend login page.
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.replace(
    `${routes.login.path}&next_frontend=${encodeURIComponent(next)}`,
  );
  return <LoadingScreen />;
};

export default observer(AuthGuard);
